import { AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Mic, Video, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

interface DeviceCheckProps {
  onReady: () => void;
}

export function DeviceCheck({ onReady }: DeviceCheckProps) {
  const { t } = useTranslation();

  const videoRef = useRef<HTMLVideoElement>(null);

  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isMicActive, setIsMicActive] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [micError, setMicError] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [isBrowserCompatible, setIsBrowserCompatible] = useState(true);
  const [showTips, setShowTips] = useState(false);

  useEffect(() => {
    const hasMediaDevices = !!navigator.mediaDevices?.getUserMedia;
    const hasMediaRecorder = typeof MediaRecorder !== 'undefined';
    setIsBrowserCompatible(hasMediaDevices && hasMediaRecorder);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let activeStream: MediaStream | null = null;
    let activeAudioContext: AudioContext | null = null;
    let activeAnimFrame = 0;

    async function initDevices() {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch (err) {
        if (!cancelled) {
          console.error('DeviceCheck: getUserMedia failed', err);
          const name = err instanceof Error ? err.name : '';
          const audioOnlyFailure = name === 'NotFoundError' || name === 'OverconstrainedError';
          setCameraError(true);
          setMicError(!audioOnlyFailure);
        }
        return;
      }

      if (cancelled) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      activeStream = stream;

      const hasVideoTrack = stream.getVideoTracks().length > 0;
      const hasAudioTrack = stream.getAudioTracks().length > 0;

      if (hasVideoTrack && videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsCameraActive(hasVideoTrack);
      setCameraError(!hasVideoTrack);

      if (!hasAudioTrack) {
        setMicError(true);
        return;
      }

      try {
        const audioContext = new AudioContext();
        activeAudioContext = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const updateLevel = (): void => {
          if (cancelled) return;
          analyser.getByteFrequencyData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i]!;
          }
          const avg = sum / dataArray.length;
          setMicLevel(Math.min(100, Math.round((avg / 128) * 100)));
          activeAnimFrame = requestAnimationFrame(updateLevel);
        };

        updateLevel();
        setIsMicActive(true);
      } catch (err) {
        if (!cancelled) {
          console.error('DeviceCheck: audio analyser setup failed', err);
          setMicError(true);
        }
      }
    }

    void initDevices();

    return () => {
      cancelled = true;
      if (activeAnimFrame) {
        cancelAnimationFrame(activeAnimFrame);
      }
      if (activeAudioContext) {
        void activeAudioContext.close();
      }
      if (activeStream) {
        activeStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const toggleTips = useCallback(() => {
    setShowTips((prev) => !prev);
  }, []);

  const isReady = isCameraActive && isMicActive;

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-xl font-bold text-gray-900'>{t('video.deviceCheck.title')}</h2>
        <p className='text-sm text-gray-500 mt-1'>{t('video.deviceCheck.subtitle')}</p>
      </div>

      {!isBrowserCompatible ? (
        <div className='flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm'>
          <AlertTriangle size={20} className='shrink-0' />
          <p>{t('video.deviceCheck.browserWarning')}</p>
        </div>
      ) : null}

      {/* Camera */}
      <div className='bg-white border rounded-lg overflow-hidden'>
        <div className='p-3 md:p-4 flex items-center justify-between border-b gap-2'>
          <div className='flex items-center gap-2 shrink-0'>
            <Video size={18} className='text-gray-500' />
            <span className='font-medium text-gray-900 text-sm md:text-base'>{t('video.deviceCheck.camera')}</span>
          </div>
          {isCameraActive ? (
            <div className='flex items-center gap-1.5 text-green-600 text-xs md:text-sm'>
              <CheckCircle size={16} className='shrink-0' />
              <span className='truncate'>{t('video.deviceCheck.cameraActive')}</span>
            </div>
          ) : cameraError ? (
            <div className='flex items-center gap-1.5 text-red-600 text-xs md:text-sm'>
              <XCircle size={16} className='shrink-0' />
              <span className='truncate'>{t('video.deviceCheck.cameraError')}</span>
            </div>
          ) : null}
        </div>
        <div className='bg-gray-900 aspect-video flex items-center justify-center'>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={cn('w-full h-full object-cover', !isCameraActive && 'hidden')}
          />
          {!isCameraActive && !cameraError ? (
            <div className='h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent' />
          ) : null}
          {cameraError ? <XCircle size={48} className='text-gray-600' /> : null}
        </div>
      </div>

      {/* Microphone */}
      <div className='bg-white border rounded-lg p-3 md:p-4'>
        <div className='flex items-center justify-between mb-3 gap-2'>
          <div className='flex items-center gap-2 shrink-0'>
            <Mic size={18} className='text-gray-500' />
            <span className='font-medium text-gray-900 text-sm md:text-base'>{t('video.deviceCheck.microphone')}</span>
          </div>
          {isMicActive ? (
            <div className='flex items-center gap-1.5 text-green-600 text-xs md:text-sm'>
              <CheckCircle size={16} className='shrink-0' />
              <span className='truncate'>{t('video.deviceCheck.micActive')}</span>
            </div>
          ) : micError ? (
            <div className='flex items-center gap-1.5 text-red-600 text-xs md:text-sm'>
              <XCircle size={16} className='shrink-0' />
              <span className='truncate'>{t('video.deviceCheck.micError')}</span>
            </div>
          ) : null}
        </div>
        <div className='h-3 bg-gray-200 rounded-full overflow-hidden'>
          <div
            className='h-full bg-green-500 rounded-full transition-all duration-100'
            style={{ width: `${micLevel}%` }}
          />
        </div>
      </div>

      {/* Troubleshooting */}
      <div className='border rounded-lg'>
        <button
          type='button'
          onClick={toggleTips}
          className='w-full flex items-center justify-between p-4 text-sm font-medium text-gray-700 hover:bg-gray-50'>
          <span>{t('video.deviceCheck.troubleshooting.title')}</span>
          {showTips ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showTips ? (
          <div className='px-4 pb-4 space-y-2 text-sm text-gray-600'>
            <p>- {t('video.deviceCheck.troubleshooting.tip1')}</p>
            <p>- {t('video.deviceCheck.troubleshooting.tip2')}</p>
            <p>- {t('video.deviceCheck.troubleshooting.tip3')}</p>
            <p>- {t('video.deviceCheck.troubleshooting.tip4')}</p>
          </div>
        ) : null}
      </div>

      {/* Continue button */}
      <div className='flex justify-end'>
        <button
          type='button'
          onClick={onReady}
          disabled={!isReady}
          className={cn(
            'w-full md:w-auto px-6 py-2.5 rounded font-medium text-sm transition-colors',
            isReady ? 'bg-primary text-white hover:bg-primary-dark' : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          )}>
          {t('video.deviceCheck.continue')}
        </button>
      </div>
    </div>
  );
}
